/**
 * Timer Hook
 * Countdown timer for problem solving
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import type { TimerOption } from '../types'

export function useTimer(duration: TimerOption, onExpire?: () => void) {
  const [timeRemaining, setTimeRemaining] = useState<number>(duration)
  const [isRunning, setIsRunning] = useState(false)
  const intervalRef = useRef<number | null>(null)

  const start = useCallback(() => {
    if (duration === 0) return // No timer
    setTimeRemaining(duration)
    setIsRunning(true)
  }, [duration])

  const stop = useCallback(() => {
    setIsRunning(false)
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const reset = useCallback(() => {
    stop()
    setTimeRemaining(duration)
  }, [duration, stop])

  // Timer countdown effect
  useEffect(() => {
    if (!isRunning || duration === 0) {
      return
    }

    intervalRef.current = window.setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setIsRunning(false)
          if (onExpire) {
            onExpire()
          }
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isRunning, duration, onExpire])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return {
    timeRemaining,
    isRunning,
    start,
    stop,
    reset,
    hasTimer: duration > 0,
  }
}
